import {
  Product,
  ProductFilters,
  ProductListResponse,
  StockMovement,
} from "../types/inventory.types";
import axiosInstance from "../../../middlewares/axiosInstance";

class ProductService {
  private readonly baseUrl = "/business-admin/products";

  async getProducts(
    filters: ProductFilters,
    page: number = 1,
    limit: number = 20
  ): Promise<ProductListResponse> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/list`, {
        search: filters.search,
        category: filters.category,
        stockStatus: filters.stockStatus,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        page,
        limit,
      });

      return response.data.data;
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  }

  async getProduct(id: string): Promise<Product> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/get`, { id });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching product:", error);
      throw error;
    }
  }

  async createProduct(data: Partial<Product>): Promise<Product> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/create`, {
        name: data.name,
        sku: data.sku,
        description: data.description,
        category: data.category,
        price: data.price,
        cost: data.cost,
        stock_quantity: data.stock_quantity,
        min_stock_level: data.minStockLevel,
        max_stock_level: data.maxStockLevel,
        unit: data.unit,
        is_active: data.is_active ?? true,
        is_composite: data.is_composite ?? false,
        is_ingredient: data.is_ingredient ?? false,
      });

      return response.data.data;
    } catch (error) {
      console.error("Error creating product:", error);
      throw error;
    }
  }

  async updateProduct(id: string, data: Partial<Product>): Promise<Product> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/update`, {
        id,
        ...data,
      });

      return response.data.data;
    } catch (error) {
      console.error("Error updating product:", error);
      throw error;
    }
  }

  async deleteProduct(id: string): Promise<void> {
    try {
      await axiosInstance.post(`${this.baseUrl}/delete`, { id });
    } catch (error) {
      console.error("Error deleting product:", error);
      throw error;
    }
  }

  async getStockMovements(
    productId: string,
    startDate?: string,
    endDate?: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    movements: StockMovement[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/stock-movements`,
        {
          productId,
          startDate,
          endDate,
          page,
          limit,
        }
      );

      return response.data.data;
    } catch (error) {
      console.error("Error fetching stock movements:", error);
      throw error;
    }
  }

  async recordStockMovement(
    productId: string,
    quantity: number,
    movementType: "IN" | "OUT" | "TRANSFER",
    sourceLocation?: string,
    destinationLocation?: string,
    notes?: string
  ): Promise<StockMovement> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/restock`, {
        product_id: productId,
        quantity,
        movement_type: movementType,
        source_location: sourceLocation,
        destination_location: destinationLocation,
        movement_date: new Date().toISOString(),
        notes,
      });

      return response.data.data;
    } catch (error) {
      console.error("Error recording stock movement:", error);
      throw error;
    }
  }

  async getCategories(): Promise<string[]> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/categories`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw error;
    }
  }
}

export const productService = new ProductService();
